import React, { useEffect, useState, useCallback } from 'react';
import { useIntl, IntlShape } from 'react-intl';
import { Skeleton, Menu, List, Dropdown, Row, Tooltip } from 'antd';
import { SettingOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { orderBy } from 'lodash';
import { TwitterService } from 'services/twitterApi';
import { useAuth } from 'hooks/auth';
import { Container, Title } from './styles';

interface Data {
  name: string;
  url: string;
  tweet_volume: number | null;
}

function buildTrendHref(url: string | undefined, name: string): string {
  const fallback = `https://twitter.com/search?q=${encodeURIComponent(name)}`;
  if (!url) return fallback;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return fallback;
    }
    return parsed.toString();
  } catch {
    return fallback;
  }
}

const WidgetHeader: React.FC<{
  setCountry: (value: string) => void;
  displayExportModal?: (value: boolean) => void;
}> = ({ setCountry, displayExportModal }) => {
  const intl = useIntl();
  const { isUserLogged } = useAuth();

  function getCountriesItems(
    i18n: IntlShape,
    countrySetter: (country: string) => void,
  ): JSX.Element {
    const items = [
      'world',
      'brazil',
      'portugal',
      'spain',
      'mexico',
      'colombia',
      'peru',
      'chile',
      'argentina'
    ].map(country => (
      <Menu.Item onClick={() => countrySetter(country)}>
        {i18n.formatMessage({ id: `trends.widget.modal.${country}` })}
      </Menu.Item>
    ));

    return <Menu>{items}</Menu>;
  }

  const countryMenu = useCallback(() => getCountriesItems(intl, setCountry), [
    intl,
    setCountry,
  ]);

  function getExportsIcon(): JSX.Element {
    if (!displayExportModal || !isUserLogged) return <span>&nbsp;</span>;

    return (
      <Tooltip title={intl.formatMessage({ id: 'trends.export.tooltip' })}>
        <CloudDownloadOutlined onClick={() => displayExportModal(true)} />
      </Tooltip>
    );
  }

  return (
    <Title>
      <strong>{intl.formatMessage({ id: 'trends.widget.x.title' })}</strong>
      <Row justify="end">
        {getExportsIcon()}
        <Dropdown overlay={countryMenu} placement="bottomLeft" arrow>
          <SettingOutlined />
        </Dropdown>
      </Row>
    </Title>
  );
};

const loadingArray = [{}, {}, {}, {}, {}] as Data[];

const Item: React.FC<Data> = ({ name, url, tweet_volume }: Data) => {
  const { locale } = useIntl();

  return (
    <List.Item>
      <a
        href={buildTrendHref(url, name)}
        target="_blank"
        rel="noopener noreferrer"
      >
        <strong>{name}</strong>
      </a>
      <span>
        {tweet_volume != null
          ? new Intl.NumberFormat(locale).format(tweet_volume)
          : ''}
      </span>
    </List.Item>
  );
};

const TwitterWidget: React.FC<{
  displayExportModal: (value: boolean) => void;
  setCountry: (value: string) => void;
  country: string;
}> = ({ ...props }) => {
  const { country, displayExportModal, setCountry } = props;
  const [data, setData] = useState<Data[]>([]);
  const [loading, setLoading] = useState(false);

  async function getTrends(countryName: string): Promise<Array<Data>> {
    const response = await TwitterService.getTrendsByCoutry(countryName);

    const [{ trends }] = response.data.body;

    return orderBy(trends, 'tweet_volume', 'desc');
  }

  useEffect(() => {
    Promise.resolve()
      .then(() => setLoading(true))
      .then(() => getTrends(country))
      .then(trends => setData(trends))
      .then(() => setLoading(false));
  }, [country]);

  return (
    <Container
      title={
        <WidgetHeader
          setCountry={setCountry}
          displayExportModal={displayExportModal}
        />
      }
    >
      <List
        dataSource={loading ? loadingArray : data}
        pagination={{ pageSize: 5 }}
        renderItem={item => (
          <Skeleton loading={loading} active paragraph={false}>
            <Item
              name={item.name}
              url={item.url}
              tweet_volume={item.tweet_volume}
            />
          </Skeleton>
        )}
      />
    </Container>
  );
};

export default TwitterWidget;
